import { useState } from "react";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import { IconBell, IconEye, IconTrash, IconWallet } from "@tabler/icons-react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  Stack,
  Typography,
} from "@mui/material";

import {
  formatPaymentStatus,
  formatDate,
  formatPrice,
  getColorByPaymentStatus,
} from "../helpers/format";

import ModalConfirm from "./ModalConfirm";

import {
  studentConfirmPayment,
  studentPaymentNotification,
} from "../services/students";
import { Empty } from "./Empty";
import { canceledInvoice } from "../services/checkout";
import { usePageModalStore } from "../stores/ModalState";
import { useMediaQuery, useTheme } from "@mui/material";

const PaymentItem = ({
  data,
  extract,
  refetch,
  isConfirmed,
  tags = false,
}: any) => {
  const { openModal } = usePageModalStore();
  const [selectedTag, setSelectedTag] = useState<string | null>("PENDING");

  const [open, setOpen] = useState({
    status: false,
    type: "PAYMENT_NOTIFICATION",
    paymentId: null,
    studentId: null,
    userId: null,
  });

  const TAGS = [
    { label: "Em aberto", value: "PENDING" },
    { label: "Pago", value: "PAID" },
    { label: "Agendado", value: "SCHEDULED" },
  ];

  const onClose = () => {
    setOpen({
      status: false,
      type: "PAYMENT_NOTIFICATION",
      paymentId: null,
      studentId: null,
      userId: null,
    });
  };

  const { mutate, isPending } = useMutation({
    mutationFn: async (body: any) => await studentPaymentNotification(body),
    onSuccess: () => {
      toast.success("Notificação enviada com sucesso!");
      onClose();
    },
    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(e.response.data.message || "Erro ao enviar notificação.");
      onClose();
    },
  });

  const { isPending: isPendingConfirmPayment, mutate: mutateConfirmPayment } =
    useMutation({
      mutationFn: async (body: any) => await studentConfirmPayment(body),
      onSuccess: () => {
        onClose();
        refetch();
      },
      onError: () => {
        toast.error("Erro ao confirmar pagamento, tente novamente mais tarde.");
      },
    });

  const { isPending: isPendingCanceledInvoice, mutate: mutateCanceledInvoice } =
    useMutation({
      mutationFn: async (id: number) => await canceledInvoice(id),
      onSuccess: () => {
        onClose();
        refetch();
        toast.success("Fatura cancelada com sucesso.");
      },
      onError: () => {
        toast.error("Erro ao cancelar fatura, tente novamente mais tarde.");
      },
    });

  const filteredPayments =
    selectedTag === null || !tags
      ? data
      : data.filter((payment: any) => {
          if (selectedTag === "PAID") {
            return [
              "PAID",
              "CONFIRMED",
              "CONFIRMED_INSTALLMENT",
              "RECEIVED_IN_CASH",
            ].includes(payment.status);
          }

          if (selectedTag === "PENDING") {
            return ["PENDING", "OVERDUE", "ERROR"].includes(payment.status);
          }
          return payment.status === selectedTag;
        });

  const sortedPayments = isConfirmed
    ? [...filteredPayments].sort(
        (a, b) =>
          new Date(a.creditDate).getTime() - new Date(b.creditDate).getTime()
      )
    : filteredPayments;

  const toggleTag = (tag: string) => {
    setSelectedTag((prev) => (prev === tag ? null : tag));
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  return (
    <>
      {tags && (
        <Stack flexDirection={"row"} gap={1} pb={1}>
          {TAGS.map((tag) => (
            <Chip
              key={tag.value}
              color={selectedTag === tag.value ? "primary" : "default"}
              onClick={() => toggleTag(tag.value)}
              label={tag.label}
            />
          ))}
        </Stack>
      )}
      {sortedPayments?.map((item: any) => (
        <Accordion key={item?.id} sx={{ mb: 1, border: "none" }}>
          <AccordionSummary
            id={`panel-${item?.id}`}
            sx={{
              paddingX: 2,
            }}
          >
            <Stack width={"100%"}>
              <Stack
                width={"100%"}
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "flex-end" }}
                sx={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <Stack sx={{ gap: 0.5 }}>
                  <Typography
                    fontWeight={700}
                    fontSize={18}
                    color={getColorByPaymentStatus(item?.status)}
                  >
                    {formatPrice(extract ? item.valueNet : item.value)}
                  </Typography>{" "}
                  {(item?.payer?.fullName || item?.payerName) && (
                    <Typography fontSize={14}>
                      <strong>
                        {item?.payer?.fullName ?? item?.payerName}
                      </strong>
                    </Typography>
                  )}
                  {item?.description && (
                    <Typography fontSize={14}>
                      <strong>{item?.description}</strong>
                    </Typography>
                  )}
                  {item.paymentDate && (
                    <Typography fontSize={14}>
                      Pago em:{" "}
                      <strong>
                        {item.paymentDate ? formatDate(item.paymentDate) : "--"}
                      </strong>
                    </Typography>
                  )}
                </Stack>

                <Stack
                  alignItems={{ xs: "flex-start", lg: "flex-end" }}
                  sx={{ gap: 0.5 }}
                >
                  {!isMobile && (
                    <Typography fontSize={18}>
                      <strong>{formatPaymentStatus(item.status)}</strong>{" "}
                    </Typography>
                  )}
                  {!isConfirmed && (
                    <Typography fontSize={14}>
                      Vencimento: <strong>{formatDate(item.dueDate)}</strong>
                    </Typography>
                  )}
                  {isConfirmed && item.billingType == "CREDIT_CARD" && (
                    <Typography fontSize={14}>
                      Previsão de recebimento:{" "}
                      <strong>{formatDate(item.creditDate)}</strong>
                    </Typography>
                  )}
                  {isMobile && (
                    <Typography fontSize={18}>
                      <strong>{formatPaymentStatus(item.status)}</strong>{" "}
                    </Typography>
                  )}
                </Stack>
              </Stack>
              <Stack>
                {!extract && item?.user?.responsableId && item?.student && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    fontSize={12}
                    fontWeight={700}
                  >
                    Esta cobrança é referente ao aluno {item?.student?.fullName}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack gap={1}>
              {["OVERDUE", "PENDING"].includes(item.status) && (
                <Button
                  variant="contained"
                  fullWidth
                  endIcon={<IconBell />}
                  sx={{ justifyContent: "space-between" }}
                  onClick={() => {
                    setOpen((i) => ({
                      ...i,
                      status: true,
                      paymentId: item.id,
                      studentId: item.studentId,
                      userId: item.userId,
                      type: "PAYMENT_NOTIFICATION",
                    }));
                  }}
                >
                  Enviar notificação
                </Button>
              )}

              {["OVERDUE", "PENDING", "SCHEDULED", "ERROR"].includes(
                item.status
              ) && (
                <Button
                  variant="contained"
                  fullWidth
                  endIcon={<IconWallet />}
                  sx={{ justifyContent: "space-between" }}
                  onClick={() => {
                    setOpen((i) => ({
                      ...i,
                      status: true,
                      paymentId: item.id,
                      studentId: item.studentId,
                      userId: item.userId,
                      type: "PAY_MONEY",
                    }));
                  }}
                >
                  Recebimento em dinheiro
                </Button>
              )}
              {![
                "PAID",
                "CONFIRMED",
                "CONFIRMED_INSTALLMENT",
                "DEBITED",
              ].includes(item.status) && (
                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  endIcon={<IconTrash />}
                  sx={{ justifyContent: "space-between" }}
                  onClick={() => {
                    setOpen((i) => ({
                      ...i,
                      status: true,
                      paymentId: item.id,
                      type: "CANCELED_INVOICE",
                    }));
                  }}
                >
                  Cancelar fatura
                </Button>
              )}

              <Button
                variant="contained"
                fullWidth
                endIcon={<IconEye />}
                sx={{ justifyContent: "space-between" }}
                // onClick={() => {
                //   navigate("/financeiro/" + item.id);
                // }}
                onClick={() => openModal({ id: item.id })}
              >
                Detalhes da transação
              </Button>
            </Stack>
          </AccordionDetails>
          {open.type === "PAYMENT_NOTIFICATION" && (
            <ModalConfirm
              title="Enviar notificação"
              description="Tem certeza de que deseja enviar uma notificação para este aluno?"
              onClose={onClose}
              onAction={() => {
                mutate({
                  studentId: open.studentId,
                  paymentId: open.paymentId,
                  userId: open.userId,
                });
              }}
              open={open.status}
              buttonColor="success"
              buttonTitle="Enviar notificação"
              loading={isPending}
            />
          )}
          {open.type === "PAY_MONEY" && (
            <ModalConfirm
              title="Recebimento em dinheiro"
              description="Tem certeza de que deseja confirmar o recebimento em dinheiro?"
              onClose={onClose}
              onAction={() => {
                mutateConfirmPayment({ id: open.paymentId });
              }}
              open={open.status}
              buttonColor="primary"
              buttonTitle="Confirmar recebimento"
              loading={isPendingConfirmPayment}
            />
          )}
          {open.type === "CANCELED_INVOICE" && (
            <ModalConfirm
              title="Cancelar fatura"
              description="Tem certeza de que deseja cancelar essa fatura?"
              onClose={onClose}
              onAction={() => {
                mutateCanceledInvoice(Number(open.paymentId));
              }}
              open={open.status}
              buttonColor="primary"
              buttonTitle="Cancelar fatura"
              loading={isPendingCanceledInvoice}
            />
          )}
        </Accordion>
      ))}

      <Empty data={filteredPayments} />
    </>
  );
};

export default PaymentItem;
